import React, { RefObject, memo, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import moment, { Moment } from "moment";
import FsLightbox from "fslightbox-react";
import { MessageStatusEnum } from "@sms/shared/message/message.interface";
import { useSelector } from "react-redux";
import { ActivePhonesData } from "@sms/shared/active-phones/active-phones.interface";

import styles from "./message-services-item.module.scss";
import { MessageServiceItemProps } from "./message-services-item.types";

import { BASE_URL } from "~/app/utils/api-fetch";
import { makeSelectUsedActivePhoneByMessageId } from "~/app/selectors/messages";
import { ApplicationStore } from "~/app/application.store";

const MessageServiceItem: React.FC<MessageServiceItemProps> = memo((props: MessageServiceItemProps) => {
  const selectUsedActivePhoneByMessageId: (state: ApplicationStore, messageId: string) => ActivePhonesData | undefined =
    useMemo(() => makeSelectUsedActivePhoneByMessageId(), undefined!);

  const activePhone: ActivePhonesData | undefined = useSelector((state: ApplicationStore) =>
    selectUsedActivePhoneByMessageId(state, props.message.id)
  );

  const [lightboxController, setLightboxController]: [
    { toggler: boolean; slide: number },
    React.Dispatch<React.SetStateAction<{ toggler: boolean; slide: number }>>
  ] = useState<{
    toggler: boolean;
    slide: number;
  }>({
    toggler: false,
    slide: 1
  });

  const openLightboxOnSlide: (number: number) => void = (number: number): void => {
    setLightboxController({
      toggler: !lightboxController.toggler,
      slide: number
    });
  };

  const ref: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && !activePhone && props.message.status === MessageStatusEnum.UNREAD) {
      const observer: IntersectionObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              props.handleMessageStatusReadUpdate(props.message);
            }
          });
        },
        { threshold: 1.0 }
      );

      observer.observe(ref.current);
      return (): void => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      };
    }
  }, [props]);

  const today: Moment = moment().endOf("day");
  const dateMessage: Moment = moment(props.message.created_at);
  const dateFormatMessage: string =
    moment(dateMessage).diff(today, "days") === 0
      ? moment(dateMessage).format("HH:mm")
      : moment(dateMessage).format("HH:mm DD.MM.YY");

  const images: string[] = props.message.media ? props.message.media?.split(",") : [];

  const imagesAddedUrl: string[] = images.map(image =>
    !image.includes("http") ? `${BASE_URL}/images/${image}` : image
  );
  const countMedias: number = images.length;

  return (
    <div className={classNames(styles.messageItem, activePhone ? styles.own : "")} ref={ref}>
      <div className={styles.messageContentWrap}>
        <div className={styles.mediaContentWrap}>
          {imagesAddedUrl.map((media, index) => {
            return (
              <div
                key={index}
                className={styles.mediaMessage}
                style={{
                  width: countMedias == 1 ? "40rem" : "19.8rem",
                  height: countMedias == 1 ? "40rem" : "19.8rem",
                  backgroundImage: `url(${media})`
                }}
                onClick={(): void => openLightboxOnSlide(index + 1)}
              ></div>
            );
          })}
        </div>

        <FsLightbox
          toggler={lightboxController.toggler}
          sources={imagesAddedUrl}
          slide={lightboxController.slide}
          type="image"
        />
        <div className={styles.messageContent}>
          <p className={styles.text}>
            {props.message.message}
            <span
              className={classNames(
                styles.messageMeta,
                props.message.status === MessageStatusEnum.ERROR ? styles.messageError : ""
              )}
            >
              {props.message.status === MessageStatusEnum.ERROR ? props.message.status : dateFormatMessage}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
});

MessageServiceItem.displayName = "MessageServiceItem";

export default MessageServiceItem;
